import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ColorPicker,
  Icon,
  IconPicker,
  Input,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import Seperator from "senf-atomic-design-system/src/components/core/seperator/Seperator";
import { Feature } from "senf-shared/mapbox";

interface GisCreateBuilderProps {
  formik: any;
  setDrawMode: (mode: any) => void;
  drawMode: any;
  featureId: string;
  initialColor: string;
  deleteFeature: (id: string) => void;
}

const GisCreateBuilder = ({
  formik,
  setDrawMode,
  drawMode,
  featureId,
  initialColor,
  deleteFeature,
}: GisCreateBuilderProps) => {
  const { t } = useTranslation();
  const number = ({ properties }: Feature) => Number(properties?.number || "0");
  const features: Feature[] = formik.values.gisData[0]?.data?.features || [];
  const [hoveredFeatureId, setHoveredFeatureId] = useState<string | null>(null);

  const handleSaveColor = async (
    feature: Feature,
    color: string,
    opacity: number
  ) => {
    const updatedFeatures = features.map((f: Feature, i: number) =>
      i === number(feature) - 1
        ? {
            ...f,
            properties: {
              ...f.properties,
              color,
              ...(opacity !== undefined && { opacity }),
            },
          }
        : f
    );

    const updatedGeoData = {
      type: "FeatureCollection",
      features: updatedFeatures,
    };

    formik.setFieldValue(`gisData[${0}].data`, updatedGeoData);
  };

  const handleSaveIcon = async (feature: Feature, icon: string | null) => {
    const updatedFeatures = features.map((f: Feature, i: number) => {
      if (i === number(feature) - 1) {
        const updatedProperties = { ...f.properties };
        if (icon) {
          updatedProperties.icon = icon;
        } else {
          delete updatedProperties.icon;
        }
        return { ...f, properties: updatedProperties };
      }
      return f;
    });
    const updatedGeoData = {
      type: "FeatureCollection",
      features: updatedFeatures,
    };

    formik.setFieldValue(`gisData[${0}].data`, updatedGeoData);
  };

  return (
    <div className="flex flex-col gap-5 pb-5">
      <p className="body-md">{t("map:builder.gisCreate.infoText")}</p>
      <div className="flex gap-2">
        <Button
          onClick={() => {
            setDrawMode({
              mode: "draw",
              drawType: "point",
            });
          }}
          variant={drawMode.drawType === "point" ? "primary" : "basic"}
          disabled={drawMode.drawType !== "point" && featureId}
          text={t("global:point")}
          leadingIcon="pin-alt"
          width="max"
          classNameWrapper="w-full"
        />
        <Button
          onClick={() => {
            setDrawMode({
              mode: "draw",
              drawType: "path",
            });
          }}
          variant={drawMode.drawType === "path" ? "primary" : "basic"}
          disabled={drawMode.drawType !== "path" && featureId}
          text={t("global:path")}
          leadingIcon="path"
          width="max"
          classNameWrapper="w-full"
        />
        <Button
          variant={drawMode.drawType === "area" ? "primary" : "basic"}
          disabled={drawMode.drawType !== "area" && featureId}
          onClick={() => {
            setDrawMode({
              mode: "draw",
              drawType: "area",
            });
          }}
          text={t("global:area")}
          leadingIcon="area"
          width="max"
          classNameWrapper="w-full"
        />
      </div>

      <Seperator className="-mx-8 w-[calc(100%+4rem)]" />

      <div className="flex flex-col gap-5 rounded-xl">
        {features?.map((feature, index) => {
          return (
            <div
              className="relative flex items-center gap-2 pr-4"
              key={index}
              onMouseEnter={() => setHoveredFeatureId(feature.id)}
              onMouseLeave={() => setHoveredFeatureId(null)}
            >
              <Icon
                className="shrink-0"
                name={
                  feature.properties.drawType === "point"
                    ? "pin-alt"
                    : feature.properties.drawType === "path"
                      ? "path"
                      : "area"
                }
              />
              <div
                key={`feature-${number(feature) - 1}`}
                className="w-full"
              >
                <Input
                  placeholder={`${feature.properties.number} ${t(
                    "global:labeling"
                  )}`}
                  name={`gisData-${feature.id}`}
                  receiveValue={(value) => {
                    const updatedFeatures = features.map((f, i) =>
                      f.id === feature?.id
                        ? {
                            ...f,
                            properties: {
                              ...f?.properties,
                              tooltipLabel: value,
                            },
                          }
                        : f
                    );

                    const updatedGeoData = {
                      type: "FeatureCollection",
                      features: updatedFeatures,
                    };

                    formik.setFieldValue(`gisData[${0}].data`, updatedGeoData);
                  }}
                  onFocus={() => {
                    // Add functionality if needed
                  }}
                  rows={1}
                  maxRows={10}
                  value={feature.properties?.tooltipLabel}
                />
              </div>
              <IconPicker
                initialColor={feature.properties?.color || initialColor}
                initialIcon={feature.properties?.icon || "IconTexture"}
                initialOpacity={feature.properties?.icon ? 1 : 0.3}
                handleSave={(icon, opacity) =>
                  handleSaveIcon(feature, icon, opacity)
                }
              />
              <ColorPicker
                initialColor={feature.properties?.color || initialColor}
                initialOpacity={feature.properties?.opacity}
                handleSave={(color, opacity) =>
                  handleSaveColor(feature, color, opacity)
                }
              />
              {hoveredFeatureId === feature.id && (
                <Button
                  leadingIcon="trash"
                  critical
                  variant="shapeless"
                  size="sm"
                  onClick={() => deleteFeature(feature.id)}
                  classNameWrapper="absolute -right-5"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GisCreateBuilder;
